import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { FroelingData, FroelingHistoryPoint, PelletDayStat } from '../version.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import { chartPalette, timeTooltipTitle, lineLegendLabels, dragZoom, relativeDayTick, timeAxisTick } from '../shared/chart.utils';

@Component({
  selector: 'app-heizung',
  templateUrl: './heizung.component.html',
})
export class HeizungComponent implements OnChanges {
  constructor(private translate: TranslateService) {}

  @Input() froeling: FroelingData | null = null;
  @Input() froelingHistory: FroelingHistoryPoint[] = [];
  @Input() froelingFlash = false;
  @Input() liveDotOn = true;
  @Input() froelingUpdatedAt: number | null = null;
  @Input() nowTick = Date.now();
  @Input() darkMode = false;
  @Input() pelletStats: PelletDayStat[] = [];
  @Output() openHeizungChart = new EventEmitter<void>();

  boilerChartData: ChartData<'line'> = { datasets: [] };
  boilerChartOptions: ChartConfiguration<'line'>['options'] = {};
  boilerExpandedData: ChartData<'line'> = { datasets: [] };
  boilerExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  bufferChartData: ChartData<'line'> = { datasets: [] };
  bufferChartOptions: ChartConfiguration<'line'>['options'] = {};
  bufferExpandedData: ChartData<'line'> = { datasets: [] };
  bufferExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  waterChartData: ChartData<'line'> = { datasets: [] };
  waterChartOptions: ChartConfiguration<'line'>['options'] = {};
  waterExpandedData: ChartData<'line'> = { datasets: [] };
  waterExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  solarThermalChartData: ChartData<'line'> = { datasets: [] };
  solarThermalChartOptions: ChartConfiguration<'line'>['options'] = {};
  solarThermalExpandedData: ChartData<'line'> = { datasets: [] };
  solarThermalExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  outdoorChartData: ChartData<'line'> = { datasets: [] };
  outdoorChartOptions: ChartConfiguration<'line'>['options'] = {};
  outdoorExpandedData: ChartData<'line'> = { datasets: [] };
  outdoorExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  pelletChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  pelletChartOptions: ChartConfiguration<'bar'>['options'] = {};
  pelletExpandedData: ChartData<'bar'> = { labels: [], datasets: [] };
  pelletExpandedOptions: ChartConfiguration<'bar'>['options'] = {};
  pelletConsumption24h = 0;

  pumpChartData: ChartData<'line'> = { datasets: [] };
  pumpChartOptions: ChartConfiguration<'line'>['options'] = {};
  pumpExpandedData: ChartData<'line'> = { datasets: [] };
  pumpExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  hcChartData: { [name: string]: ChartData<'line'> } = {};
  hcChartOptions: ChartConfiguration<'line'>['options'] = {};
  hcExpandedData: { [name: string]: ChartData<'line'> } = {};
  hcExpandedOptions: ChartConfiguration<'line'>['options'] = {};

  readonly PELLET_CAPACITY_KG = 10_000;

  // Per-tab time range: 'day' uses the live 24h history; longer ranges fetch the
  // downsampled long-term archive and drive every heizung chart.
  range: 'day' | 'week' | 'month' | 'year' = 'day';
  rangeLoading = false;
  froelingRangeHistory: FroelingHistoryPoint[] = [];
  readonly chartRanges: { key: 'day' | 'week' | 'month' | 'year'; label: string }[] = [
    { key: 'day',   label: 'Tag' },
    { key: 'week',  label: 'Woche' },
    { key: 'month', label: 'Monat' },
    { key: 'year',  label: 'Jahr' },
  ];
  private static readonly RANGE_MS: Record<string, number> = {
    day:   24 * 3600 * 1000,
    week:  7 * 24 * 3600 * 1000,
    month: 30 * 24 * 3600 * 1000,
    year:  365 * 24 * 3600 * 1000,
  };

  get displayFroeling(): FroelingHistoryPoint[] {
    return this.range === 'day' ? this.froelingHistory : this.froelingRangeHistory;
  }
  get rangeLabel(): string {
    return this.chartRanges.find(r => r.key === this.range)?.label ?? '';
  }

  setRange(range: 'day' | 'week' | 'month' | 'year'): void {
    this.range = range;
    if (range === 'day') { this.rebuild(); return; }
    const to   = Date.now();
    const from = to - HeizungComponent.RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=froeling&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => { this.froelingRangeHistory = pts ?? []; this.rangeLoading = false; this.rebuild(); })
      .catch(() => { this.rangeLoading = false; });
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  boilerStateLabel(state: string | undefined): string {
    if (!state) return '';
    const map: Record<string, string> = {
      '0': 'Störung', '1': 'Kessel Aus', '2': 'Anheizen', '3': 'Heizen',
      '4': 'Feuererhaltung', '5': 'Feuer Aus', '6': 'Tür offen',
      '7': 'Vorbereitung', '8': 'Vorwärmen', '9': 'Zünden',
      '10': 'Abstellen', '11': 'Abstellen', '12': 'Abstellen',
      '13': 'Abstellen', '14': 'Abstellen', '15': 'Abreinigen',
      '16': '2h warten', '17': 'Saugen/Heizen', '18': 'Fehlzündung',
      '19': 'Betriebsbereit',
    };
    return map[state.trim()] ?? state;
  }

  hcLabel(name: string): string {
    const match = name.match(/(\d+)/);
    return match ? this.translate.instant('HEIZKREIS') + ' ' + match[1] : name;
  }

  pumpLabel(state: string | undefined): string {
    if (!state) return '';
    const s = state.toLowerCase().trim();
    if (s === 'ein' || s === 'on' || s === 'an' || s === '1' || s === 'true') return 'An';
    if (s === 'aus' || s === 'off' || s === '0' || s === 'false') return 'Aus';
    return state;
  }

  private pumpToNumber(state: string | undefined): number {
    if (!state) return 0;
    const s = state.toLowerCase().trim();
    if (s === 'ein' || s === 'on' || s === 'an' || s === '1' || s === 'true') return 1;
    return 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only rebuild charts when the underlying data/theme changes — NOT on every
    // liveDotOn (600ms) / nowTick (1000ms) tick, which would otherwise re-map the
    // whole history and reassign every chart object continuously.
    if (!changes['froelingHistory'] && !changes['froeling']
        && !changes['darkMode'] && !changes['pelletStats']) return;
    this.rebuild();
  }

  private rebuild(): void {
    this.buildPelletBarChart();
    const pts = this.displayFroeling;
    if (pts.length < 2) return;
    const { textColor, gridColor } = chartPalette(this.darkMode);
    // x window spans the selected range (now-windowMs … now) so date labels show
    // across a Woche/Monat even when the archive is still sparse. 'Tag' = relative ticks.
    const xMax = Date.now();
    const xMin = xMax - HeizungComponent.RANGE_MS[this.range];
    const xTick = this.range === 'day' ? relativeDayTick : timeAxisTick(xMin, xMax);

    const buildOptions = (expanded: boolean, yLabel: string, yUnit: string): ChartConfiguration<'line'>['options'] => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
      plugins: {
        legend: expanded
          ? { display: true, labels: lineLegendLabels(textColor) }
          : { display: false },
        tooltip: {
          callbacks: {
            title: timeTooltipTitle,
            label: (item) => ` ${(item.parsed.y ?? 0).toFixed(1)} ${yUnit}`,
          },
        },
        ...(expanded ? { zoom: dragZoom() } : {}),
      },
      scales: {
        x: {
          display: true,
          type: 'linear',
          min: xMin,
          max: xMax,
          ticks: {
            color: textColor,
            maxTicksLimit: expanded ? 8 : 3,
            font: { size: expanded ? 11 : 10 },
            maxRotation: 0,
            callback: xTick,
          },
          grid: { display: expanded, color: gridColor },
        },
        y: {
          display: true,
          ticks: {
            color: textColor,
            maxTicksLimit: expanded ? 6 : 4,
            font: { size: expanded ? 11 : 10 },
            callback: (val) => `${(val as number).toFixed(0)} ${yUnit}`,
          },
          grid: { color: gridColor, display: expanded },
          title: { display: expanded, text: yLabel, color: textColor, font: { size: 11 } },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line: { borderWidth: expanded ? 2 : 1.5, tension: 0.4 },
      },
    });

    const buildDualAxisOptions = (expanded: boolean, yLabel: string, yUnit: string, y1Label: string): ChartConfiguration<'line'>['options'] => {
      const base = buildOptions(expanded, yLabel, yUnit)!;
      return {
        ...base,
        plugins: {
          ...base.plugins,
          tooltip: {
            callbacks: {
              title: timeTooltipTitle,
              label: (item) => {
                if ((item.dataset as any).yAxisID === 'y1') {
                  return ` ${item.dataset.label}: ${item.parsed.y ? 'Ein' : 'Aus'}`;
                }
                return ` ${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(1)} ${yUnit}`;
              },
            },
          },
        },
        scales: {
          ...base.scales,
          y1: {
            position: 'right' as const,
            min: -0.1,
            max: 1.1,
            display: expanded,
            ticks: {
              color: textColor,
              font: { size: 10 },
              maxTicksLimit: 3,
              callback: (val: any) => val === 1 ? 'Ein' : val === 0 ? 'Aus' : '',
            },
            grid: { drawOnChartArea: false },
            title: { display: expanded, text: y1Label, color: textColor, font: { size: 11 } },
          },
        },
      };
    };

    // Standard line dataset from a point accessor — dedupes the repeated
    // borderColor / transparent-bg / no-fill boilerplate across the charts below.
    const lineDs = (label: string, accessor: (p: FroelingHistoryPoint) => number,
                    color: string, opts: any = {}): any => ({
      label,
      data: pts.map(p => ({ x: p.ts, y: accessor(p) })),
      borderColor: color,
      backgroundColor: 'transparent',
      fill: false,
      ...opts,
    });

    // Boiler chart: temp + exhaust
    const boilerData = (expanded: boolean): ChartData<'line'> => ({
      datasets: [
        lineDs('Kessel', p => p.boilerTempC, '#dc3545', { borderWidth: expanded ? 2 : 1.5 }),
        ...(expanded ? [lineDs('Abgas', p => p.exhaustTempC, '#fd7e14', { borderWidth: 2, borderDash: [4, 3] })] : []),
      ],
    });
    this.boilerChartData = boilerData(false);
    this.boilerChartOptions = buildOptions(false, 'Temperatur', '°C');
    this.boilerExpandedData = boilerData(true);
    this.boilerExpandedOptions = buildOptions(true, 'Temperatur (°C)', '°C');

    // Buffer chart: top + bottom
    const bufferData = (expanded: boolean): ChartData<'line'> => ({
      datasets: [
        lineDs('Oben', p => p.bufferTopTempC, '#dc3545', { borderWidth: expanded ? 2 : 1.5 }),
        ...(expanded ? [lineDs('Unten', p => p.bufferBottomTempC, '#0d6efd', { borderWidth: 2 })] : []),
      ],
    });
    this.bufferChartData = bufferData(false);
    this.bufferChartOptions = buildOptions(false, 'Temperatur', '°C');
    this.bufferExpandedData = bufferData(true);
    this.bufferExpandedOptions = buildOptions(true, 'Puffer (°C)', '°C');

    // Hot water chart
    const waterData = (): ChartData<'line'> => ({ datasets: [lineDs('Warmwasser', p => p.hotWaterTempC, '#0dcaf0')] });
    this.waterChartData = waterData();
    this.waterChartOptions = buildOptions(false, 'Temperatur', '°C');
    this.waterExpandedData = waterData();
    this.waterExpandedOptions = buildOptions(true, 'Warmwasser (°C)', '°C');

    // Solar thermal chart
    const solarData = (): ChartData<'line'> => ({ datasets: [lineDs('Kollektor', p => p.solarThermalTempC, '#ffc107')] });
    this.solarThermalChartData = solarData();
    this.solarThermalChartOptions = buildOptions(false, 'Temperatur', '°C');
    this.solarThermalExpandedData = solarData();
    this.solarThermalExpandedOptions = buildOptions(true, 'Solarthermie (°C)', '°C');

    // Outdoor temperature chart
    const outdoorData = (): ChartData<'line'> => ({ datasets: [lineDs('Außen', p => p.outdoorTempC, '#6c757d')] });
    this.outdoorChartData = outdoorData();
    this.outdoorChartOptions = buildOptions(false, 'Temperatur', '°C');
    this.outdoorExpandedData = outdoorData();
    this.outdoorExpandedOptions = buildOptions(true, 'Außentemperatur (°C)', '°C');

    // Pellet 24h consumption from history
    if (pts.length >= 2) {
      this.pelletConsumption24h = Math.max(0,
        (pts[pts.length - 1].pelletConsumptionKg ?? 0) - (pts[0].pelletConsumptionKg ?? 0));
    }

    // Circulation pump chart
    const hasPumpData = pts.some(p => (p.circulationPumpPct ?? 0) > 0);
    if (hasPumpData) {
      const pumpData = (): ChartData<'line'> => ({
        datasets: [lineDs('Pumpe %', p => p.circulationPumpPct ?? 0, '#0d6efd',
          { backgroundColor: 'rgba(13,110,253,0.15)', fill: true })],
      });
      this.pumpChartData = pumpData();
      this.pumpChartOptions = buildOptions(false, 'Drehzahl', '%');
      this.pumpExpandedData = pumpData();
      this.pumpExpandedOptions = buildOptions(true, 'Pumpendrehzahl (%)', '%');
    }

    // Heating circuits charts — with pump state on right axis
    const circuits = this.froeling?.heatingCircuits ?? [];
    if (circuits.length > 0) {
      this.hcChartOptions = buildOptions(false, 'Temperatur', '°C');
      this.hcExpandedOptions = buildDualAxisOptions(true, 'Vorlauf (°C)', '°C', 'Pumpe');
      const hcColors = ['#198754', '#6610f2', '#d63384', '#20c997'];
      for (let i = 0; i < circuits.length; i++) {
        const key: string = circuits[i].name;
        const color = hcColors[i % hcColors.length];
        this.hcChartData[key] = {
          datasets: [{
            label: key,
            data: pts.filter(p => p.heatingCircuits?.[i]).map(p => ({ x: p.ts, y: p.heatingCircuits[i].flowTempActualC })),
            borderColor: color,
            backgroundColor: 'transparent',
            fill: false,
          }],
        };
        this.hcExpandedData[key] = {
          datasets: [
            {
              label: 'Vorlauf Ist',
              data: pts.filter(p => p.heatingCircuits?.[i]).map(p => ({ x: p.ts, y: p.heatingCircuits[i].flowTempActualC })),
              borderColor: color,
              backgroundColor: 'transparent',
              fill: false,
              borderWidth: 2,
            },
            {
              label: 'Pumpe',
              data: pts.filter(p => p.heatingCircuits?.[i]).map(p => ({ x: p.ts, y: this.pumpToNumber(p.heatingCircuits[i].pumpState) })),
              borderColor: '#ff9800',
              backgroundColor: 'rgba(255,152,0,0.1)',
              fill: true,
              borderWidth: 1.5,
              borderDash: [3, 3] as number[],
              stepped: 'before' as const,
              yAxisID: 'y1',
            },
          ],
        };
      }
    }
  }

  private buildPelletBarChart(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const recentPellet = this.pelletStats.slice(-7);
    if (recentPellet.length === 0) return;
    const labels = recentPellet.map(d => {
      const dt = new Date(d.date);
      return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    });
    const values = recentPellet.map(d => d.consumptionKg);
    const barOptions = (expanded: boolean): ChartConfiguration<'bar'>['options'] => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => ` ${(item.parsed.y ?? 0).toFixed(1)} kg`,
          },
        },
      },
      scales: {
        x: {
          display: true,
          ticks: { color: textColor, font: { size: expanded ? 11 : 10 }, maxRotation: 0 },
          grid: { display: false },
        },
        y: {
          display: true,
          beginAtZero: true,
          ticks: {
            color: textColor,
            maxTicksLimit: expanded ? 6 : 4,
            font: { size: expanded ? 11 : 10 },
            callback: (val) => `${(val as number).toFixed(0)} kg`,
          },
          grid: { color: gridColor, display: expanded },
          title: { display: expanded, text: 'Verbrauch (kg)', color: textColor, font: { size: 11 } },
        },
      },
    });
    this.pelletChartData = { labels, datasets: [{ label: 'kg/Tag', data: values, backgroundColor: '#795548', borderRadius: 3 }] };
    this.pelletChartOptions = barOptions(false);
    this.pelletExpandedData = { labels, datasets: [{ label: 'kg/Tag', data: values, backgroundColor: '#795548', borderRadius: 4 }] };
    this.pelletExpandedOptions = barOptions(true);
  }
}
